import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { clientsAPI, usersAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  RefreshCw,
  Check,
  X,
  User,
  Phone,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  FileText,
  ArrowRight
} from "lucide-react";
import { cn } from "../lib/utils";

const REASON_CODE_LABELS = {
  CLIENT_REQUEST: "Solicitud del cliente",
  SERVICE_QUALITY: "Calidad de servicio",
  NO_RESPONSE: "Sin respuesta del dueño",
  OTHER: "Otro motivo"
};

const STATUS_CONFIG = {
  pending: { label: "Pendiente", color: "bg-amber-100 text-amber-800", icon: Clock },
  approved: { label: "Aprobada", color: "bg-green-100 text-green-800", icon: CheckCircle },
  rejected: { label: "Rechazada", color: "bg-red-100 text-red-800", icon: XCircle }
};

export default function AdminReasignaciones() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  
  // Dialog states
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(null); // "approve" or "reject"
  const [processing, setProcessing] = useState(false);
  
  // Action form
  const [actionForm, setActionForm] = useState({
    new_owner_id: "",
    admin_comment: ""
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [requestsRes, vendedoresRes] = await Promise.all([
        clientsAPI.getAllReassignmentRequests({ status: activeTab === "all" ? "" : activeTab }),
        usersAPI.getVendedores()
      ]);
      setRequests(requestsRes.data);
      setVendedores(vendedoresRes.data);
    } catch (error) {
      toast.error("Error al cargar solicitudes");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openActionDialog = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setActionForm({
      new_owner_id: action === "approve" ? request.requested_by_user_id : "",
      admin_comment: ""
    });
    setActionDialogOpen(true);
  };

  const handleAction = async (e) => {
    e.preventDefault();
    
    if (actionType === "approve" && !actionForm.new_owner_id) {
      toast.error("Debe seleccionar el nuevo dueño");
      return;
    }
    if (actionType === "reject" && !actionForm.admin_comment.trim()) {
      toast.error("Debe especificar el motivo del rechazo");
      return;
    }
    
    setProcessing(true);
    try {
      await clientsAPI.processReassignmentRequest(selectedRequest.id, {
        action: actionType,
        new_owner_id: actionType === "approve" ? actionForm.new_owner_id : null,
        admin_comment: actionForm.admin_comment || null
      });
      
      toast.success(
        actionType === "approve" 
          ? "Reasignación aprobada exitosamente"
          : "Solicitud rechazada"
      );
      setActionDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al procesar solicitud");
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status];
    if (!config) return <Badge>{status}</Badge>;
    const Icon = config.icon;
    return (
      <Badge className={cn(config.color, "flex items-center gap-1")}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  // Stats
  const pendingCount = requests.filter(r => r.status === "pending").length;
  const approvedCount = requests.filter(r => r.status === "approved").length;
  const rejectedCount = requests.filter(r => r.status === "rejected").length;

  // Access check
  if (user?.role !== "admin") {
    return (
      <Layout>
        <div className="p-6 lg:p-8">
          <Card className="max-w-md mx-auto">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
              <h2 className="text-lg font-semibold">Acceso Restringido</h2>
              <p className="text-slate-500 mt-2">
                Solo los administradores pueden acceder a esta sección.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="admin-reasignaciones-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">
            Solicitudes de Reasignación
          </h1>
          <p className="text-slate-600">
            Revisa y procesa solicitudes de cambio de dueño de clientes
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className={cn(
            "border-l-4",
            pendingCount > 0 ? "border-l-amber-500" : "border-l-slate-300"
          )}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pendientes</p>
                  <p className="text-3xl font-bold text-amber-600">{pendingCount}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-300" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Aprobadas</p>
                  <p className="text-3xl font-bold text-green-600">{approvedCount}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-300" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Rechazadas</p>
                  <p className="text-3xl font-bold text-red-600">{rejectedCount}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-300" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs and Table */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pendientes {pendingCount > 0 && `(${pendingCount})`}
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">
              Aprobadas
            </TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">
              Rechazadas
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">
              Todas
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            <Card>
              {loading ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
                </div>
              ) : requests.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No hay solicitudes {activeTab !== "all" ? STATUS_CONFIG[activeTab]?.label.toLowerCase() + "s" : ""}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Dueño Actual</TableHead>
                      <TableHead>Solicitante</TableHead>
                      <TableHead>Motivo</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Estado</TableHead>
                      {activeTab === "pending" && <TableHead className="text-right">Acciones</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((req) => (
                      <TableRow key={req.id} data-testid={`request-row-${req.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{req.client_name}</p>
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {req.client_phone}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <span>{req.current_owner_name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-blue-500" />
                            <span className="text-blue-700 font-medium">{req.requested_by_name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <Badge variant="outline" className="mb-1">
                              {REASON_CODE_LABELS[req.reason_code] || req.reason_code}
                            </Badge>
                            {req.reason_detail && (
                              <p className="text-xs text-slate-500 max-w-xs truncate" title={req.reason_detail}>
                                {req.reason_detail}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Calendar className="w-3 h-3" />
                            {req.created_at?.slice(0, 10)}
                          </div>
                          {req.processed_at && (
                            <p className="text-xs text-slate-400 mt-1">
                              Procesada: {req.processed_at?.slice(0, 10)}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(req.status)}</TableCell>
                        {activeTab === "pending" && (
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 hover:bg-green-50"
                                onClick={() => openActionDialog(req, "approve")}
                                data-testid={`approve-btn-${req.id}`}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Aprobar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => openActionDialog(req, "reject")}
                                data-testid={`reject-btn-${req.id}`}
                              >
                                <X className="w-4 h-4 mr-1" />
                                Rechazar
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>
        </Tabs>

        {/* Action Dialog */}
        <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {actionType === "approve" ? "Aprobar Reasignación" : "Rechazar Solicitud"}
              </DialogTitle>
            </DialogHeader>
            
            {selectedRequest && (
              <form onSubmit={handleAction} className="space-y-4">
                {/* Request Summary */}
                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">{selectedRequest.client_name}</p>
                    <Badge variant="outline">{REASON_CODE_LABELS[selectedRequest.reason_code]}</Badge>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-slate-400" />
                    <span>{selectedRequest.current_owner_name}</span>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                    <span className="text-blue-600 font-medium">{selectedRequest.requested_by_name}</span>
                  </div>
                  {selectedRequest.reason_detail && (
                    <p className="text-sm text-slate-600 mt-2 p-2 bg-white rounded border">
                      "{selectedRequest.reason_detail}"
                    </p>
                  )}
                </div>
                
                {actionType === "approve" && (
                  <div className="space-y-2">
                    <Label>Nuevo Dueño *</Label>
                    <Select 
                      value={actionForm.new_owner_id} 
                      onValueChange={(v) => setActionForm(prev => ({ ...prev, new_owner_id: v }))}
                    >
                      <SelectTrigger data-testid="new-owner-select">
                        <SelectValue placeholder="Seleccionar nuevo dueño..." />
                      </SelectTrigger>
                      <SelectContent>
                        {vendedores.map(v => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.name} {v.role === "admin" ? "(Admin)" : ""}
                            {v.id === selectedRequest.requested_by_user_id && (
                              <Badge className="ml-2 text-xs" variant="secondary">Solicitante</Badge>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      Por defecto se sugiere al vendedor solicitante
                    </p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>
                    {actionType === "approve" ? "Comentario (opcional)" : "Motivo del rechazo *"}
                  </Label>
                  <Textarea
                    value={actionForm.admin_comment}
                    onChange={(e) => setActionForm(prev => ({ ...prev, admin_comment: e.target.value }))}
                    placeholder={
                      actionType === "approve" 
                        ? "Agregar nota opcional..."
                        : "Explique por qué se rechaza la solicitud..."
                    }
                    required={actionType === "reject"}
                    data-testid="admin-comment-textarea"
                  />
                </div>
                
                {actionType === "approve" && (
                  <div className="p-3 bg-green-50 rounded-lg text-sm text-green-700">
                    <p className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      El cliente será reasignado al nuevo dueño seleccionado
                    </p>
                  </div>
                )}
                
                {actionType === "reject" && (
                  <div className="p-3 bg-red-50 rounded-lg text-sm text-red-700">
                    <p className="flex items-center gap-2">
                      <XCircle className="w-4 h-4" />
                      El cliente permanecerá con el dueño actual
                    </p>
                  </div>
                )}
                
                <DialogFooter>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setActionDialogOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={processing}
                    className={actionType === "reject" ? "bg-red-600 hover:bg-red-700" : ""}
                    data-testid="submit-action-btn"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Procesando...
                      </>
                    ) : actionType === "approve" ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Aprobar Reasignación
                      </>
                    ) : (
                      <>
                        <X className="w-4 h-4 mr-2" />
                        Rechazar Solicitud
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
